"use client";

import type { ButtonHTMLAttributes, ReactNode } from "react";

type Tone = "default" | "wood";

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  active?: boolean;
  tone?: Tone;
  icon?: ReactNode;
};

/**
 * Chip — pill-shaped filter / segmented control.
 * Acid-tinted when active (default tone), wood-tinted when tone="wood"
 * (used for XP-stake / trust badges).
 */
export function Chip({
  active = false,
  tone = "default",
  icon,
  className = "",
  children,
  ...rest
}: Props) {
  const base =
    "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors";

  let tonal: string;
  if (active && tone === "default") {
    tonal =
      "bg-[rgba(198,255,61,0.08)] border-[var(--color-acid-dim)] text-[var(--color-acid)]";
  } else if (tone === "wood") {
    tonal =
      "bg-[rgba(212,165,116,0.08)] border-[var(--color-wood-dark)] text-[var(--color-wood)]";
  } else {
    tonal =
      "bg-[var(--color-surface-2)] border-[var(--color-border)] text-[var(--color-text-muted)]";
  }

  return (
    <button type="button" className={`${base} ${tonal} ${className}`} {...rest}>
      {icon}
      {children}
    </button>
  );
}
